import React from 'react';
import { buildCharacterInventoryRows } from '../utils/CharacterSheetReferencePresentation';

const ITEM_TYPES = ['item', 'weapon', 'armor', 'shield', 'consumable', 'tool', 'ammunition'];
const RARITIES = ['', 'common', 'uncommon', 'rare', 'very rare', 'legendary', 'artifact'];
const PRINT_LOCATIONS = [
  ['', 'Automatic'],
  ['head', 'Head & Neck'],
  ['body', 'Body'],
  ['back', 'Back & Shoulders'],
  ['hands', 'Hands & Arms'],
  ['waist', 'Waist & Carried'],
  ['feet', 'Feet & Legs']
];

const formatLabel = (value, fallback = 'Item') => {
  const normalized = String(value || '').trim();
  if (!normalized) return fallback;
  return normalized
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

/**
 * Full-embed inventory editor shared by the character sheet and its visual-test harness.
 * date_of_change: 2026-07-29
 * rule_reference: Canonical equipment keeps equipment, magic-item descriptions, equipped
 * state, attunement requirements, and current attunement state in one persisted record.
 */
const CharacterInventorySection = ({ character, setCharacter, isPresentationMode = false }) => {
  const updateCharacterArrayEntry = (sourceCollection, sourceIndex, patch) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const path = String(sourceCollection || '').split('.').filter(Boolean);
      if (path.length === 0) return prev;

      const next = { ...prev };
      let sourceNode = prev;
      let targetNode = next;
      path.slice(0, -1).forEach((segment) => {
        const sourceValue = sourceNode?.[segment];
        const normalizedSource = segment === 'equipment' && Array.isArray(sourceValue)
          ? { items: sourceValue }
          : sourceValue;
        targetNode[segment] = normalizedSource && typeof normalizedSource === 'object' && !Array.isArray(normalizedSource)
          ? { ...normalizedSource }
          : {};
        sourceNode = normalizedSource;
        targetNode = targetNode[segment];
      });

      const collectionKey = path[path.length - 1];
      const sourceArray = Array.isArray(sourceNode?.[collectionKey]) ? sourceNode[collectionKey] : [];
      const nextArray = [...sourceArray];
      const existing = nextArray[sourceIndex];
      const existingObject = typeof existing === 'string' ? { name: existing } : { ...(existing || {}) };
      nextArray[sourceIndex] = { ...existingObject, ...patch };
      targetNode[collectionKey] = nextArray;
      return next;
    });
  };

  const appendEquipmentItem = (item) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const equipment = prev.equipment && typeof prev.equipment === 'object' && !Array.isArray(prev.equipment)
        ? prev.equipment
        : {};
      const items = Array.isArray(equipment.items) ? equipment.items : [];
      return {
        ...prev,
        equipment: {
          ...equipment,
          items: [...items, item]
        }
      };
    });
  };

  const removeEquipmentItem = (sourceCollection, sourceIndex) => {
    setCharacter((prev) => {
      if (!prev) return prev;
      const path = String(sourceCollection || '').split('.').filter(Boolean);
      if (path.length === 0) return prev;
      const next = { ...prev };
      let sourceNode = prev;
      let targetNode = next;
      path.slice(0, -1).forEach((segment) => {
        const sourceValue = sourceNode?.[segment];
        targetNode[segment] = sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue)
          ? { ...sourceValue }
          : {};
        sourceNode = sourceValue;
        targetNode = targetNode[segment];
      });
      const collectionKey = path[path.length - 1];
      const sourceArray = Array.isArray(sourceNode?.[collectionKey]) ? sourceNode[collectionKey] : [];
      targetNode[collectionKey] = sourceArray.filter((_, index) => index !== sourceIndex);
      return next;
    });
  };

  const renderItemDrawer = (item, isMagicItem) => {
    const itemName = item.name || 'Unnamed item';
    const summaryMeta = [
      isMagicItem && item.rarity ? formatLabel(item.rarity) : null,
      formatLabel(item.type),
      `Qty ${item.quantity}`,
      item.weight ? String(item.weight) : null
    ].filter(Boolean);

    return (
      <details
        key={item.key}
        className={`character-sheet-item-drawer${isMagicItem ? ' character-sheet-item-drawer--magic' : ''}${item.attuned ? ' character-sheet-item-drawer--attuned' : ''}`}
        aria-label={`${itemName} details`}
      >
        <summary className="character-sheet-item-summary">
          <span className="character-sheet-item-summary-copy">
            <span className="character-sheet-item-summary-name">{itemName}</span>
            <span className="character-sheet-item-summary-meta">{summaryMeta.join(' · ')}</span>
          </span>
          <span className="character-sheet-item-summary-status">
            {item.equipped && <span>Equipped</span>}
            {isMagicItem && item.attuned && <span>Attuned</span>}
            {isMagicItem && item.requiresAttunement && !item.attuned && <span>Attunement</span>}
          </span>
        </summary>

        <div className="character-sheet-item-drawer-content">
          <div className="character-sheet-item-fields">
            <label className="character-sheet-item-field character-sheet-item-field--name">
              <span>Item name</span>
              <input
                type="text"
                defaultValue={item.name}
                aria-label={`${itemName} name`}
                onChange={(event) => updateCharacterArrayEntry(item.sourceCollection, item.sourceIndex, { name: event.target.value })}
              />
            </label>

            <label className="character-sheet-item-field">
              <span>Type</span>
              <select
                value={ITEM_TYPES.includes(item.type) ? item.type : 'item'}
                aria-label={`${itemName} type`}
                onChange={(event) => updateCharacterArrayEntry(item.sourceCollection, item.sourceIndex, { type: event.target.value })}
              >
                {ITEM_TYPES.map((type) => (
                  <option key={type} value={type}>{formatLabel(type)}</option>
                ))}
              </select>
            </label>

            {isMagicItem && (
              <label className="character-sheet-item-field">
                <span>Rarity</span>
                <select
                  value={item.rarity}
                  aria-label={`${itemName} rarity`}
                  onChange={(event) => updateCharacterArrayEntry(item.sourceCollection, item.sourceIndex, { rarity: event.target.value })}
                >
                  {RARITIES.map((rarity) => (
                    <option key={rarity || 'unspecified'} value={rarity}>
                      {rarity ? formatLabel(rarity) : 'Unspecified'}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="character-sheet-item-field character-sheet-item-field--quantity">
              <span>Qty</span>
              <input
                type="number"
                min="1"
                defaultValue={item.quantity}
                aria-label={`${itemName} quantity`}
                onChange={(event) => updateCharacterArrayEntry(item.sourceCollection, item.sourceIndex, {
                  quantity: Math.max(1, parseInt(event.target.value, 10) || 1)
                })}
              />
            </label>

            <label className="character-sheet-item-field character-sheet-item-field--weight">
              <span>Weight</span>
              <input
                type="text"
                defaultValue={item.weight}
                placeholder="0 lb"
                aria-label={`${itemName} weight`}
                onChange={(event) => updateCharacterArrayEntry(item.sourceCollection, item.sourceIndex, { weight: event.target.value })}
              />
            </label>

            <label className="character-sheet-item-field">
              <span>Print location</span>
              <select
                value={item.printLocation || ''}
                aria-label={`${itemName} print location`}
                title="Controls the printed equipment figure only."
                onChange={(event) => updateCharacterArrayEntry(item.sourceCollection, item.sourceIndex, { printLocation: event.target.value })}
              >
                {PRINT_LOCATIONS.map(([value, label]) => <option key={value || 'automatic'} value={value}>{label}</option>)}
              </select>
            </label>
          </div>

          <div className="character-sheet-item-state" aria-label={`${itemName} state`}>
            <label>
              <input
                type="checkbox"
                checked={item.equipped}
                aria-label={`${itemName} equipped`}
                onChange={(event) => updateCharacterArrayEntry(item.sourceCollection, item.sourceIndex, { equipped: event.target.checked })}
              />
              Equipped
            </label>
            <label>
              <input
                type="checkbox"
                checked={item.magical}
                aria-label={`${itemName} is a magic item`}
                onChange={(event) => updateCharacterArrayEntry(item.sourceCollection, item.sourceIndex, {
                  magical: event.target.checked,
                  ...(event.target.checked ? {} : {
                    rarity: '',
                    requiresAttunement: false,
                    attunement: false,
                    attuned: false
                  })
                })}
              />
              Magic item
            </label>
            {isMagicItem && (
              <>
                <label>
                  <input
                    type="checkbox"
                    checked={item.requiresAttunement}
                    aria-label={`${itemName} requires attunement`}
                    onChange={(event) => updateCharacterArrayEntry(item.sourceCollection, item.sourceIndex, {
                      requiresAttunement: event.target.checked,
                      attunement: event.target.checked,
                      ...(event.target.checked ? {} : { attuned: false })
                    })}
                  />
                  Requires attunement
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={item.attuned}
                    aria-label={`${itemName} attuned`}
                    onChange={(event) => updateCharacterArrayEntry(item.sourceCollection, item.sourceIndex, {
                      attuned: event.target.checked,
                      ...(event.target.checked ? { requiresAttunement: true, attunement: true } : {})
                    })}
                  />
                  Attuned
                </label>
              </>
            )}
          </div>

          <label className="character-sheet-item-description">
            <span>Description</span>
            <textarea
              rows={3}
              defaultValue={item.description}
              aria-label={`${itemName} description`}
              placeholder="Rules text, command words, charges, and notes."
              onChange={(event) => updateCharacterArrayEntry(item.sourceCollection, item.sourceIndex, { description: event.target.value })}
            />
          </label>
          <label className="character-sheet-item-description">
            <span>Properties</span>
            <textarea
              rows={2}
              value={(item.properties || []).join('\n')}
              aria-label={`${itemName} properties`}
              placeholder="One property per line."
              onChange={(event) => updateCharacterArrayEntry(item.sourceCollection, item.sourceIndex, {
                properties: event.target.value.split('\n').map((property) => property.trim()).filter(Boolean)
              })}
            />
          </label>
          <button
            type="button"
            className="character-sheet-item-remove"
            onClick={() => removeEquipmentItem(item.sourceCollection, item.sourceIndex)}
          >
            Remove {itemName}
          </button>
        </div>
      </details>
    );
  };

  const inventoryRows = buildCharacterInventoryRows(character);
  const equipmentRows = inventoryRows.filter((item) => !item.magical);
  const magicItemRows = inventoryRows.filter((item) => item.magical);
  const attunedMagicItemCount = magicItemRows.filter((item) => item.attuned).length;

  return (
    <div className="character-sheet-inventory character-sheet-full-width-section">
      <section className="character-sheet-inventory-section" aria-labelledby="equipment-section-title">
        <div className="character-sheet-inventory-section-heading">
          <h2 id="equipment-section-title" className="section-header-dnd">Equipment</h2>
          <div className="character-sheet-inventory-section-actions">
            <span className="character-sheet-inventory-count">{equipmentRows.length}</span>
            {!isPresentationMode && (
              <button
                type="button"
                className="character-sheet-inventory-add"
                onClick={() => appendEquipmentItem({
                  name: 'New Equipment',
                  type: 'item',
                  description: '',
                  quantity: 1,
                  weight: '',
                  magical: false,
                  equipped: false
                })}
              >
                Add equipment
              </button>
            )}
          </div>
        </div>

        {equipmentRows.length > 0 ? (
          <div className="character-sheet-item-grid">
            {equipmentRows.map((item) => renderItemDrawer(item, false))}
          </div>
        ) : (
          <p className="character-sheet-inventory-empty">No equipment recorded.</p>
        )}
      </section>

      <section className="character-sheet-inventory-section character-sheet-inventory-section--magic" aria-labelledby="magic-items-section-title">
        <div className="character-sheet-inventory-section-heading">
          <h2 id="magic-items-section-title" className="section-header-dnd">Magic Items</h2>
          <div className="character-sheet-inventory-section-actions">
            <span className="character-sheet-inventory-count">{magicItemRows.length}</span>
            <span className="character-sheet-inventory-attuned-count">{attunedMagicItemCount} attuned</span>
            {!isPresentationMode && (
              <button
                type="button"
                className="character-sheet-inventory-add"
                onClick={() => appendEquipmentItem({
                  name: 'New Magic Item',
                  type: 'item',
                  description: '',
                  quantity: 1,
                  weight: '',
                  rarity: '',
                  magical: true,
                  requiresAttunement: false,
                  attunement: false,
                  attuned: false,
                  equipped: false
                })}
              >
                Add magic item
              </button>
            )}
          </div>
        </div>

        {magicItemRows.length > 0 ? (
          <div className="character-sheet-item-grid">
            {magicItemRows.map((item) => renderItemDrawer(item, true))}
          </div>
        ) : (
          <p className="character-sheet-inventory-empty">No magic items recorded.</p>
        )}
      </section>
    </div>
  );
};

export default CharacterInventorySection;
